/**
 * Branding edit permissions — single source of truth for the client.
 * Must mirror canManageBranding() in appwrite-functions/team-branding-manager.
 *
 * Editable when:
 *   1. The team is a partner team — Appwrite Team preference `partner` = true.
 *   AND
 *   2. The user carries the teamadmin / admin / superadmin Appwrite label,
 *      OR the user has the `owner` role in the team membership.
 *
 * Everyone else sees view-only Branding with the Brainiacs default logo and
 * colour — no upload, no changes.
 */

/** User labels allowed to change team branding. */
export const BRANDING_ADMIN_LABELS = ['teamadmin', 'admin', 'superadmin'] as const;
/** Team membership roles that allow branding changes. */
const BRANDING_ALLOWED_ROLES = ['owner'] as const;

/**
 * The `partner` flag is written to Appwrite Team prefs as the STRING "true"
 * (same convention as the mod_* flags) — accept both the string and a real
 * boolean so partner teams actually get edit access.
 */
const isPartnerTeam = (teamPrefs?: Record<string, any> | null): boolean =>
  teamPrefs?.partner === true || teamPrefs?.partner === 'true';

export const canManageBranding = (
  membershipRoles: readonly string[] = [],
  userLabels?: readonly string[],
  /** Raw team prefs — the `partner` flag decides branding availability. */
  teamPrefs?: Record<string, any> | null,
): boolean =>
  isPartnerTeam(teamPrefs) &&
  (BRANDING_ADMIN_LABELS.some((label) => userLabels?.includes(label)) ||
   BRANDING_ALLOWED_ROLES.some((role) => membershipRoles.includes(role)));
